'use client';

/**
 * HandoffQueueCard — shown inline at the tail of the transcript while a
 * live-agent handoff sits in HandoffPhase.WAITING (see page.tsx, rendered
 * the same way ContactInfoCard renders for COLLECTING — same "lives in the
 * conversation lane, not a modal" precedent).
 *
 * Deliberately has NO right-hand pill. The Figma mock shows a queue-position
 * pill ("#2 · ~3 min"), but there is no backend signal for either a queue
 * position or an ETA — fabricating one would mislead the user (the same
 * call presence-ribbon-text.ts's TIMED_OUT copy already makes: no invented
 * countdown/position anywhere in this flow). Omitted, not deferred.
 *
 * The progress track below is an INDETERMINATE sweep for the identical
 * reason: a fixed partial-width fill reads as a measurable "% complete",
 * which this UI has no data to back up. The sweeping bar reuses the
 * existing `patience-sweep` keyframe (tailwind.config.ts, built for
 * call-notice.tsx's PATIENCE track) rather than a new animation, since the
 * visual language — a highlight drifting across a flat track — is the same
 * "we're waiting, not measuring" signal in both places.
 */
import { V3_COLORS } from '@/lib/design-tokens';
import { StandardCard } from './primitives/standard-card';
import { ClockIcon } from './primitives/handoff-icons';

export function HandoffQueueCard() {
  return (
    <StandardCard
      icon={<ClockIcon />}
      eyebrow="IN QUEUE"
      title="A guide is on the way"
    >
      <div className="flex flex-col gap-[10px]">
        <div
          className="h-[6px] w-full overflow-hidden rounded-[4px]"
          style={{ backgroundColor: '#F3E9DA' }}
        >
          {/* Reduced motion: a static, FULL-width muted fill — not a
                partial one. A partial static bar would itself look like a
                specific (frozen) percentage, exactly the misleading signal
                this component avoids; a full flat fill just says "active",
                nothing more. Reuses V3_COLORS.amberTile rather than a new
                literal — see design-tokens.ts's own "2+ places" rule (this
                token already backs call-notice.tsx's WARNING tile and
                model-busy-ribbon.tsx's EXHAUSTED tile). */}
          <div
            aria-hidden="true"
            className="hidden h-full w-full rounded-[4px] motion-reduce:block"
            style={{ backgroundColor: V3_COLORS.amberTile }}
          />
          {/* Default: an indeterminate sweep. Width/position are
                irrelevant to the "don't imply measured progress" rule
                because the bar never stops — it's continuously in motion,
                so it can't be read as a snapshot of completion. */}
          <div
            aria-hidden="true"
            className="h-full w-[42%] rounded-[4px] motion-safe:animate-patience-sweep motion-reduce:hidden"
            style={{ background: 'linear-gradient(90deg,#FFC759,#FF8F42)' }}
          />
        </div>
        <div>
          A guide will join this chat shortly — no need to refresh or leave.
          Feel free to keep adding context below while you wait.
        </div>
      </div>
    </StandardCard>
  );
}
